import Swal from "sweetalert2";
import {
  build_campaign_form_data,
} from "../DonationCampaigns/donation_campaigns_form_helper";

function get_form_message_errors(error) {
  const response_data = error?.responseJSON || error;
  return response_data?.message && typeof response_data.message !== "string"
    ? response_data.message
    : null;
}

function get_existing_or_saved_campaign(saved_campaign, campaigns, campaign_id) {
  if (saved_campaign?.id) {
    return saved_campaign;
  }

  return campaigns.find((campaign) => Number(campaign.id) === Number(campaign_id)) || {};
}

function get_supporting_upload_handler(instance) {
  return instance.editing_campaign_id
    ? () => instance.upload_supporting_documents_for_campaign(instance.editing_campaign_id)
    : () => instance.create_campaign_before_supporting_upload();
}

export function build_campaign_preview(campaign_form, get_category_name, calculate_days_left) {
  const form = campaign_form || {};

  return {
    id: "preview",
    title: get_campaign_preview_title(form),
    description: get_campaign_preview_description(form),
    category: get_category_name(form.category_id),
    goal_amount: get_campaign_preview_goal_amount(form),
    current_amount: 0,
    status: "draft",
    cover_image: get_campaign_preview_cover_image(form),
    supporting_files_count: get_campaign_preview_supporting_files_count(form),
    start_date: form.start_date,
    end_date: form.end_date,
    days_left: calculate_days_left(form.end_date),
  };
}

function get_campaign_preview_title(form) {
  return form.title || "Your campaign title";
}

function get_campaign_preview_description(form) {
  return (
    form.description ||
    "Use the campaign story area to explain the campaign and what the funds will support."
  );
}

function get_campaign_preview_goal_amount(form) {
  return Number(form.goal_amount || 0);
}

function get_campaign_preview_cover_image(form) {
  return form.cover_image_preview || "";
}

function get_campaign_preview_supporting_files_count(form) {
  return Array.isArray(form.campaign_document_files) ? form.campaign_document_files.length : 0;
}

function show_supporting_upload_success() {
  Swal.fire({
    icon: "success",
    title: "Supporting files uploaded.",
    showConfirmButton: false,
    timer: 2200,
    position: "bottom-end",
    toast: true,
  });
}

function show_campaign_image_saved(is_updating_existing_campaign) {
  Swal.fire({
    icon: "success",
    title: is_updating_existing_campaign ? "Campaign image saved." : "Campaign created with image.",
    showConfirmButton: false,
    timer: 2200,
    position: "bottom-end",
    toast: true,
  });
}

export const my_donation_campaigns_flow_methods = {
  refresh_after_supporting_upload(campaign_id, current_campaign) {
    return Promise.allSettled([this.fetch_my_campaigns_api()]).then(() => {
      const refreshed_campaign = this.my_campaigns_data.find(
        (campaign) => Number(campaign.id) === Number(campaign_id)
      ) || current_campaign;

      this.selected_campaign_id = refreshed_campaign?.id || campaign_id;
      if (refreshed_campaign?.id) {
        this.populate_form_from_campaign(refreshed_campaign);
      }
    });
  },
  complete_supporting_upload_success(campaign_id, current_campaign) {
    this.editing_campaign_id = campaign_id;
    this.campaign_document_files = [];

    this.refresh_after_supporting_upload(campaign_id, current_campaign).then(() => {
      show_supporting_upload_success();
    });
  },
  handle_form_request_error(error, fallback_message) {
    const message_errors = get_form_message_errors(error);
    if (message_errors) {
      this.set_form_errors(message_errors);
      return true;
    }

    Swal.fire({
      icon: "error",
      title: this.get_api_error_message(error, fallback_message),
      showConfirmButton: false,
      timer: 2500,
      position: "bottom-end",
      toast: true,
    });
    return false;
  },
  upload_supporting_documents_for_campaign(campaign_id) {
    const form_data = this.build_supporting_documents_form_data(campaign_id);

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/upload_campaign_supporting_documents/`,
      method: "POST",
      headers: this.get_request_headers(),
      data: form_data,
      processData: false,
      contentType: false,
      success: (response_data) => {
        const saved_campaign = response_data?.data || {};
        const current_campaign = get_existing_or_saved_campaign(
          saved_campaign,
          this.my_campaigns_data,
          campaign_id
        );

        if (saved_campaign?.id) {
          this.sync_campaign_lists(saved_campaign);
        }

        this.complete_supporting_upload_success(campaign_id, current_campaign);
      },
      error: (error) => {
        this.handle_form_request_error(error, "Unable to upload supporting files.");
      },
      complete: () => {
        this.is_uploading_supporting_files = false;
      },
    });
  },
  create_campaign_before_supporting_upload() {
    const form_data = build_campaign_form_data(this.campaign_form);

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/create_campaign/`,
      method: "POST",
      headers: this.get_request_headers(),
      data: form_data,
      processData: false,
      contentType: false,
      success: (response_data) => {
        const saved_campaign = response_data?.data || {};
        this.sync_campaign_lists(saved_campaign);
        this.editing_campaign_id = saved_campaign.id;
        this.populate_form_from_campaign(saved_campaign);
        this.upload_supporting_documents_for_campaign(saved_campaign.id);
      },
      error: (error) => {
        this.handle_form_request_error(
          error,
          "Unable to create campaign before uploading supporting files."
        );
        this.is_uploading_supporting_files = false;
      },
    });
  },
  upload_campaign_cover() {
    if (!should_upload_campaign_cover(this)) {
      return;
    }

    const form_data = prepare_campaign_cover_form_data(this);
    if (!form_data) {
      return;
    }

    execute_campaign_cover_upload(this, form_data);
  },
  upload_campaign_supporting_files() {
    if (!this.token_exists || !this.campaign_document_files.length) {
      return;
    }

    this.clear_form_errors();
    if (!this.has_campaign_title_for_file_upload()) {
      this.set_supporting_file_title_error();
      return;
    }

    this.is_uploading_supporting_files = true;
    get_supporting_upload_handler(this)();
  },
};

function get_campaign_cover_request_url(editing_campaign_id) {
  return editing_campaign_id
    ? `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/update_campaign/`
    : `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/create_campaign/`;
}

function should_upload_campaign_cover(instance) {
  if (!has_campaign_cover_upload_context(instance)) {
    return false;
  }

  instance.clear_form_errors();

  if (has_campaign_cover_title(instance)) {
    return true;
  }

  mark_campaign_cover_title_error(instance);
  return false;
}

function has_campaign_cover_upload_context(instance) {
  return Boolean(instance.token_exists && instance.campaign_form.cover_image);
}

function has_campaign_cover_title(instance) {
  return Boolean(String(instance.campaign_form.title || "").trim());
}

function mark_campaign_cover_title_error(instance) {
  instance.errors.title = "Campaign title is required before uploading an image.";
}

function prepare_campaign_cover_form_data(instance) {
  const form_data = instance.build_campaign_form_data();

  if (instance.editing_campaign_id) {
    form_data.append("campaign_id", instance.editing_campaign_id);
  }

  return form_data;
}

function execute_campaign_cover_upload(instance, form_data) {
  const is_updating_existing_campaign = Boolean(instance.editing_campaign_id);

  instance.is_uploading_campaign_image = true;

  $.ajax({
    url: get_campaign_cover_request_url(instance.editing_campaign_id),
    method: "POST",
    headers: instance.get_request_headers(),
    data: form_data,
    processData: false,
    contentType: false,
    success: (response_data) => {
      const saved_campaign = response_data?.data || {};
      instance.sync_campaign_lists(saved_campaign);
      instance.editing_campaign_id = saved_campaign.id;
      instance.populate_form_from_campaign(saved_campaign);
      show_campaign_image_saved(is_updating_existing_campaign);
    },
    error: (error) => {
      instance.handle_form_request_error(error, "Unable to upload campaign image.");
    },
    complete: () => {
      instance.is_uploading_campaign_image = false;
    },
  });
}
